/**
 * @typedef {Object} GuidedProjectStartAssessment
 * @property {string} title
 * @property {string} refined_problem_statement
 * @property {string} current_condition
 * @property {string} project_objective
 * @property {string} business_impact
 * @property {string} scope
 * @property {{metric_name: string, issue_definition: string, counting_rule: string, measurement_basis: string, validation_notes: string[]}} operational_definition
 * @property {string[]} ctqs
 * @property {string[]} stakeholder_requirements
 * @property {{in_scope: string[], out_of_scope: string[], assumptions: string[], constraints: string[], risks: string[], notes: string[]}} scope_guardrails
 * @property {string[]} problem_classification
 * @property {string[]} impact_areas
 * @property {string[]} initial_ctqs
 * @property {string[]} critical_gaps
 * @property {string} confidence_level
 * @property {{field: string, category: string, status: string, reason: string}[]} missing_information
 * @property {{field: string, category: string, priority: string, question: string}[]} follow_up_questions
 * @property {string[]} assumptions_or_unvalidated_items
 * @property {string} suggested_title
 * @property {string} quantified_problem
 * @property {string} suggested_metric
 * @property {string} estimated_baseline
 * @property {string} suggested_goal
 * @property {string[]} risk_factors
 * @property {string} recommended_scope
 * @property {string[]} suggested_kpis
 * @property {string[]} clarifying_questions
 */

function toStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

function normalizeScopeGuardrails(value) {
  const payload = value && typeof value === "object" ? value : {};
  const notes = toStringArray(payload.notes || [...toStringArray(payload.assumptions), ...toStringArray(payload.constraints), ...toStringArray(payload.risks)]);
  return {
    in_scope: toStringArray(payload.in_scope),
    out_of_scope: toStringArray(payload.out_of_scope),
    assumptions: toStringArray(payload.assumptions),
    constraints: toStringArray(payload.constraints),
    risks: toStringArray(payload.risks),
    notes,
  };
}

function normalizeOperationalDefinition(value) {
  const payload = value && typeof value === "object" ? value : {};
  return {
    metric_name: String(payload.metric_name || payload.metric || "").trim(),
    issue_definition: String(payload.issue_definition || payload.definition || "").trim(),
    counting_rule: String(payload.counting_rule || payload.count_rule || "").trim(),
    measurement_basis: String(payload.measurement_basis || payload.measurement_method || payload.basis || "").trim(),
    validation_notes: toStringArray(payload.validation_notes || payload.notes),
  };
}

function normalizeMissingInformation(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (item && typeof item === "object" ? {
      field: String(item.field || "").trim(),
      category: String(item.category || "").trim(),
      status: String(item.status || "").trim(),
      reason: String(item.reason || "").trim(),
    } : null))
    .filter((item) => item && item.field && item.category && item.status && item.reason);
}

function normalizeFollowUpQuestions(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (item && typeof item === "object" ? {
      field: String(item.field || "").trim(),
      category: String(item.category || "").trim(),
      priority: String(item.priority || "medium").trim(),
      question: String(item.question || "").trim(),
    } : null))
    .filter((item) => item && item.field && item.question);
}

/**
 * Normalize guided project-start responses so the UI can rely on a structured
 * Black Belt assessment shape while staying backward-compatible with older
 * summary-style payloads.
 *
 * @param {unknown} raw
 * @param {string} fallbackProblem
 * @returns {GuidedProjectStartAssessment}
 */
export function normalizeGuidedProjectStartResponse(raw, fallbackProblem = "") {
  const payload = raw && typeof raw === "object" ? raw : {};
  const title = String(payload.title || payload.suggested_title || fallbackProblem.slice(0, 60) || "").trim();
  const refinedProblemStatement = String(
    payload.refined_problem_statement || payload.quantified_problem || fallbackProblem || "",
  ).trim();
  const currentCondition = String(
    payload.current_condition || payload.estimated_baseline || "Current baseline performance must be validated in Measure.",
  ).trim();
  const projectObjective = String(
    payload.project_objective || payload.suggested_goal || "Clarify the performance gap, validate the baseline in Measure, and set the final target after the baseline is confirmed.",
  ).trim();
  const businessImpact = String(
    payload.business_impact || "Business impact should be estimated if available and confirmed during Define and Measure.",
  ).trim();
  const scope = String(
    payload.scope || payload.recommended_scope || "Scope boundaries should be clarified in Define, including what is in scope, out of scope, and where the process begins and ends.",
  ).trim();
  const operationalDefinition = normalizeOperationalDefinition(payload.operational_definition);
  const ctqs = toStringArray(payload.ctqs || payload.initial_ctqs || payload.suggested_kpis);
  const stakeholderRequirements = toStringArray(payload.stakeholder_requirements);
  const scopeGuardrails = normalizeScopeGuardrails(payload.scope_guardrails);
  const problemClassification = toStringArray(payload.problem_classification);
  const impactAreas = toStringArray(payload.impact_areas);
  const initialCtqs = toStringArray(payload.initial_ctqs || payload.suggested_kpis || ctqs);
  const criticalGaps = toStringArray(payload.critical_gaps || payload.clarifying_questions);
  const confidenceLevel = String(payload.confidence_level || "medium").trim().toLowerCase() || "medium";
  const missingInformation = normalizeMissingInformation(payload.missing_information);
  const followUpQuestions = normalizeFollowUpQuestions(payload.follow_up_questions);
  const assumptionsOrUnvalidatedItems = toStringArray(payload.assumptions_or_unvalidated_items || scopeGuardrails.notes);
  const suggestedMetric = String(payload.suggested_metric || "").trim();
  const estimatedBaseline = String(payload.estimated_baseline || "Baseline to be validated in Measure phase").trim();
  const suggestedGoal = String(
    payload.suggested_goal || "Improve the process outcome after Measure validates the baseline; set the final target then",
  ).trim();
  const riskFactors = toStringArray(payload.risk_factors || criticalGaps);
  const recommendedScope = String(payload.recommended_scope || "To be defined in Define phase").trim();

  return {
    title,
    refined_problem_statement: refinedProblemStatement,
    current_condition: currentCondition,
    project_objective: projectObjective,
    business_impact: businessImpact,
    scope,
    operational_definition: operationalDefinition,
    ctqs,
    stakeholder_requirements: stakeholderRequirements,
    scope_guardrails: scopeGuardrails,
    problem_classification: problemClassification,
    impact_areas: impactAreas,
    initial_ctqs: initialCtqs,
    critical_gaps: criticalGaps,
    confidence_level: confidenceLevel,
    missing_information: missingInformation,
    follow_up_questions: followUpQuestions,
    assumptions_or_unvalidated_items: assumptionsOrUnvalidatedItems,
    suggested_title: title,
    quantified_problem: refinedProblemStatement,
    suggested_metric: suggestedMetric,
    estimated_baseline: estimatedBaseline,
    suggested_goal: suggestedGoal,
    risk_factors: riskFactors,
    recommended_scope: recommendedScope,
    suggested_kpis: initialCtqs,
    clarifying_questions: criticalGaps,
  };
}
